import { ReactNode, createContext, useLayoutEffect, useState } from "react";
import "./sidebar";
import classNames from "classnames";
import useScreenSize from "@shared/utility/screen-size";

export const SidebarContext = createContext({ expanded: false });

export interface SidebarProps {
  children: ReactNode;
  title: string;
  logo: string;
  minifiedWidthClass?: string;
  logoClass?: string;
  logoWidth?: string;
  icon?: string;
  className?: string;
  username?: string;
  userRole?: string;
  userDepartment?: string;
  logoutElement?: ReactNode;
  userInfoElement?: ReactNode;
  headerLeftElement?: ReactNode;
  headerRightElement?: ReactNode;
  navClass?: string;
  onLogout?: () => void;
  onUserInfo?: () => void;
}

export function Sidebar({
  children,
  className,
  title,
  logo = "https://img.logoipsum.com/243.svg",
  minifiedWidthClass = "w-[68px]",
  logoWidth = "w-36",
  logoClass,
  username,
  userRole,
  userDepartment,
  logoutElement,
  userInfoElement,
  headerLeftElement,
  headerRightElement,
  navClass = 'bg-white',
  onLogout,
  onUserInfo,
}: SidebarProps) {
  const [expanded, setExpanded] = useState(true);
  const { isBelowContentWidth } = useScreenSize();

  useLayoutEffect(() => {
    if (isBelowContentWidth) {
      setExpanded(false);
    }
  }, [isBelowContentWidth]);

  return (
    <aside
      className={classNames(
        "h-screen hidden md:flex",
        expanded ? "w-[300px]" : minifiedWidthClass,
        className
      )}
    >
      <nav
        className={classNames(
          "fixed h-full flex flex-col",
          navClass
        )}
      >
        <div className="px-5 pt-4 mb-2 flex justify-between items-center">
          <div className={classNames("flex items-center gap-2")}>
            <img
              src={logo}
              className={classNames(
                `overflow-hidden transition-all`,
                expanded ? logoWidth : "w-0",
                logoClass
              )}
              alt={title}
            />
            <span className={classNames(expanded ? "visible" : "hidden")}>
              {title}
            </span>
          </div>
          <button
            onClick={() => setExpanded((curr) => !curr)}
            className="px-3 py-3 rounded-lg bg-gray-50 hover:bg-gray-100"
          >
            {expanded && headerLeftElement
              ? headerLeftElement
              : headerRightElement || ">"}
          </button>
        </div>

        <SidebarContext.Provider value={{ expanded }}>
          <ul className="flex-1 px-5 mt-2">{children}</ul>
        </SidebarContext.Provider>

        <div className="border-t flex p-3 items-center justify-center">
          {logoutElement && onLogout ? (
            <span
              className="cursor-pointer hover:bg-gray-200 rounded"
              onClick={() => onLogout()}
            >
              {logoutElement}
            </span>
          ) : null}
          {expanded && (
            <div
              className={`
              flex justify-between items-center
              overflow-hidden transition-all w-52 ml-3
          `}
            >
              <div className="leading-4 text-left">
                {username && <h4 className="font-semibold">{username}</h4>}
                {userRole && (
                  <span className="text-sm text-gray-800">{userRole}</span>
                )}
                {userDepartment && (
                  <span className="text-xs text-gray-400 block">
                    {userDepartment}
                  </span>
                )}
              </div>
              {userInfoElement && onUserInfo ? (
                <span
                  className="cursor-pointer hover:bg-gray-200 rounded"
                  onClick={() => onUserInfo()}
                >
                  {userInfoElement}
                </span>
              ) : null}
            </div>
          )}
        </div>
      </nav>
    </aside>
  );
}

export default Sidebar;
